import DomainModel from "./DomainModel";
import * as dm from './domain';
import logging from './logging';

const log = logging('DefaultsManager');


export default class DefaultsManager {
    defaults: dm.Defaults;
    material: dm.MaterialDefaults;
    style: dm.StyleDefaults;

    constructor(defaults: dm.Defaults) {
        this.defaults = defaults;
        this.material = defaults.material;
        this.style = defaults.style;

        this.reset = this.reset.bind(this);
    }

    get(ctor: typeof DomainModel, props: Record<string, any>): DomainModel
    {
        const material = this.material;
        const style = this.style;
        switch(ctor) {
            case dm.ClosetUnit:
                break;

            case dm.Cubby:
                return dm.Cubby.fromJSON({
                    faceMaterial: material.edgeband,
                    boxMaterial: material.shelving,
                    ...props,
                });

            case dm.Divider:
                return dm.Divider.fromJSON({
                    materials: [
                        material.edgeband,
                        material.shelving,
                    ]
                });

            case dm.Door:
                return dm.Door.fromJSON({
                    frame: style.doorFrame
                        ? {
                            style: style.doorFrame,
                            material: material.door,
                        }
                        : null,
                    panel: {
                        style: style.doorPanel,
                        grain: "vertical",
                        material: material.door,
                    },
                    handles:
                        props.hinge == 'double'
                        ? [
                            {
                                style: templ(style.hardware, 'v'),
                                materials: [material.hardware],
                            },
                            {
                                style: templ(style.hardware, 'v'),
                                materials: [material.hardware],
                            },
                          ]
                        : [
                            {
                                style: templ(
                                    style.hardware,
                                    props.hinge == 'left' || props.hinge == 'right'
                                    ? 'v' : 'h'),
                                materials: [material.hardware]
                            },
                          ],
                    ...props,
                });

            case dm.HookRack:
                break;

            case dm.VerticalPanel:
                return dm.VerticalPanel.fromJSON({
                    materials: [material.shelving],
                    ...props,
                });

            case dm.Drawer:
                return dm.Drawer.fromJSON({
                    frame: props.scoop || style.drawerFrame == null ? null : {
                        style: style.drawerFrame,
                        material: material.drawer,
                    },
                    panel: props.scoop
                        ? {
                            style: "",
                            grain: "horizontal",
                            material: material.drawerBox
                          }
                        : {
                            style: style.drawerPanel,
                            grain: "horizontal",
                            material: material.drawer
                           },
                    handles: props.scoop
                        ? []
                        : [{
                            position: { x: 9, y: props.height/2 - 0.25, z: 0},
                            style: templ(style.hardware, 'h'),
                            materials: [material.hardware],
                          }],
                    box: {
                        material: material.drawerBox,
                    },
                    ...props,
                });

            case dm.DrawerBox:
                return dm.DrawerBox.fromJSON({
                    materials: [material.drawerBox],
                    ...props,
                });

            case dm.GenericAsset: {
                let materials: object;
                if(props.filename.indexOf("TAG") == -1) {
                    materials = [];
                } else {
                    const [_tag, name, ..._extra] = props.filename.split("_");
                    switch(name) {
                        case "LaundryOrganizer":
                            materials = [
                                {type: "GlossBlack"},
                                material.tagMetal,
                                material.tagFabric];
                            break;
                        case "PantRack":
                        case "ShoeOrganizer":
                            materials = [material.tagMetal, {type: "GlossBlack"}];
                            break;
                        default:
                            materials = [material.tagMetal, material.tagFabric]
                    }
                }
                return dm.GenericAsset.fromJSON({materials, ...props});
            }

            // accepts a 'direction' property, either 'h' or 'v' to fill in the
            // handle filename template
            case dm.Handle:
                let hinge = '';
                if(props.hinge == 'right') hinge = 'r';

                return dm.Handle.fromJSON({
                    materials: [material.hardware],
                    style: templ(style.hardware, String(props.direction), hinge),
                    ...props,
                });

            case dm.Lights:
                return dm.Lights.fromJSON({
                    materials:
                        props.type == 'strip'
                        ? [material.light]
                        : [material.hardware, material.light],
                    ...props,
                });

            case dm.ParametricPart:
                const materials = {
                    'HangBar': [material.rod],
                    'GlassShelf': [{type: 'Glass'}],
                    'AdjustableShelf': [
                        material.shelving,
                        material.edgeband,
                    ],
                    'FlatShoeShelf': [
                        material.shelving,
                        material.edgeband,
                    ],
                    'FixedShoeShelf': [
                        material.shelving,
                        material.edgeband,
                    ],
                    'SlidingShelf': [
                        material.shelving,
                        material.edgeband,
                        {type: 'Chrome'},
                    ],
                    'SlantedShelfWithFence': [
                        material.shelving,
                        material.edgeband,
                        material.hardware,
                    ],
                }[props.name] || [material.shelving];
                return dm.ParametricPart.fromJSON({
                    materials,
                    ...props,
                });

            case dm.Wall:
                break;

            default:
                log.error('DefaultsManager get(): no default material for model');
        }
    }

    reset(model: DomainModel) {
        const material = this.material;
        const style = this.style;

        const ctor = Object.getPrototypeOf(model).constructor;
        switch(ctor) {
            case dm.ClosetUnit: {
                const unit = model as dm.ClosetUnit;
                unit.materials = [material.shelving, material.edgeband];
                break;
            }
            case dm.Cubby: {
                const cubby = model as dm.Cubby;
                cubby.faceMaterial = material.edgeband;
                cubby.boxMaterial = material.shelving;
                break;
            }
            case dm.Divider: {
                const divider = model as dm.Divider;
                divider.materials = [material.shelving, material.edgeband];
                break;
            }
            case dm.Door: {
                const door = model as dm.Door;
                if(style.doorFrame == null) {
                    door.frame = null;
                } else {
                    door.frame = dm.Frame.fromJSON({
                        style: style.doorFrame,
                        material: material.door,
                    }, door);
                }

                door.panel.style = style.doorPanel;
                door.panel.material = material.door;

                let handle: dm.Handle;
                if(door.handles.length > 0) {
                    handle = dm.Handle.fromJSON(door.handles[0], door);
                } else {
                    handle = dm.Handle.fromJSON({}, door);
                }
                handle.style = templ(
                        style.hardware,
                        door.hinge == 'left' || door.hinge == 'right'
                        ? 'v' : 'h');
                handle.materials = [material.hardware];
                if(door.hinge == 'double') {
                    const left = handle;
                    const right = handle.clone(true);
                    door.handles = [left, right];
                } else {
                    door.handles = [handle];
                }

                break;
            }
            case dm.Drawer: {
                const drawer = model as dm.Drawer;
                if(style.drawerFrame == null) {
                    drawer.frame = null;
                } else {
                    drawer.frame = dm.Frame.fromJSON({
                        style: style.drawerFrame,
                        material: material.drawer,
                    }, drawer);
                }

                drawer.panel.style = style.drawerPanel;
                drawer.panel.material = material.drawer;

                let handle: dm.Handle;
                if(drawer.handles.length > 0) {
                    // we assume that normally the handle.side property is null
                    // to imply the default position opposite the hinge. The
                    // one likely other case would be if a cabinet has a full
                    // width pull at the top or bottom, in which case they
                    // both have the same setting.
                    handle = dm.Handle.fromJSON(drawer.handles[0], drawer);
                } else {
                    handle = dm.Handle.fromJSON({}, drawer);
                }
                handle.style = templ(style.hardware, 'h');
                handle.materials = [material.hardware];
                drawer.handles = [handle];

                break;
            }
            case dm.DrawerBox: {
                const box = model as dm.DrawerBox;
                box.material = material.drawerBox;
                break;
            }
            case dm.GenericAsset: {
                const asset = model as dm.GenericAsset;
                if(asset.filename.indexOf("TAG") == -1) {
                    asset.materials = [];
                } else {
                    const [_tag, name, ..._extra] = asset.filename.split("_");
                    switch(name) {
                        case "LaundryOrganizer":
                            asset.materials = [
                                dm.Material.fromJSON({type: "GlossBlack"}),
                                material.tagMetal,
                                material.tagFabric];
                            break;
                        case "PantRack":
                        case "ShoeOrganizer":
                            asset.materials = [
                                material.tagMetal,
                                dm.Material.fromJSON({type: "GlossBlack"})
                            ];
                            break;
                        default:
                            asset.materials = [material.tagMetal, material.tagFabric]
                    }
                }
                break;
            }
            case dm.Lights: {
                const lights = model as dm.Lights;
                lights.materials = lights.type == 'strip'
                    ? [material.light]
                    : [material.hardware, material.light];
                break;
            }
            case dm.ParametricPart: {
                const part = model as dm.ParametricPart;
                switch(part.name) {
                    case 'HangBar': 
                        part.materials = [material.rod];
                        break;
                    case 'GlassShelf':
                        part.materials = [dm.Material.fromJSON({type: 'Glass'})];
                        break;
                    case 'FixedShoeShelf':
                    case 'FlatShoeShelf':
                    case 'AdjustableShelf':
                        part.materials = [material.shelving, material.edgeband];
                        break;
                    case 'SlidingShelf':
                        part.materials = [
                            material.shelving,
                            material.edgeband,
                            dm.Material.fromJSON({type: 'Chrome'})
                        ];
                        break;
                    case 'SlantedShelfWithFence':
                        part.materials = [
                            material.shelving,
                            material.edgeband,
                            material.hardware,
                        ];
                        break;

                    default:
                        part.materials = [material.shelving];
                }
            }
            // There is no default case because this is expected to be called
            // by the DomainModel.traverse function, in which case we will
            // visit some items we don't need to modify at all.
        }
    }
}

// Probably a lot more concise ways of writing this, but I think this is the
// clearest I can come up with. All # characters will be consumed unless they
// are escaped with a leading backslash, i.e. \#. Each additional argument is
// substituted for a # in the input until arguments run out, then the # is
// merely deleted an no error is logged.
const templ = (template: string, ...args: string[]): string => {
    let result = "";
    let i = 0;
    const segments = template.split(/(?<!\\)#/g);
    if(segments.length == 1) {
        return template;
    }
    for(const segment of segments) {
        result += segment;
        if(i < args.length) {
            result += args[i];
            i += 1;
        }
    }
    return result;
}
